import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CommunityComment,
  CommunityPost,
  CommunityPostCreatePayload,
  CommunityPostListResponse,
  CommunityPostUpdatePayload,
  CommunityTopic,
  CommunityTrendingTopic,
  CommunityDirectMessage,
  CommunityDirectMessageThread,
} from '../types';
import {
  CommunityCommentPayload,
  CommunityFeedParams,
  createCommunityComment,
  createCommunityPost,
  deleteCommunityComment,
  deleteCommunityPost,
  fetchCommunityComments,
  fetchCommunityPost,
  fetchCommunityPosts,
  fetchCommunityTopics,
  fetchTrendingTopics,
  followAuthor,
  likeCommunityComment,
  likeCommunityPost,
  unlikeCommunityComment,
  unfollowAuthor,
  unlikeCommunityPost,
  updateCommunityPost,
  ensureDirectMessageThread,
  fetchDirectMessageThreads,
  fetchDirectMessages,
  sendDirectMessage,
} from '../services/communityService';

export type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

type CommentsEntry = {
  items: CommunityComment[];
  status: AsyncStatus;
  error: string | null;
  createStatus: AsyncStatus;
  createError: string | null;
  likePending: Record<string, boolean>;
};

type DirectMessageEntry = {
  items: CommunityDirectMessage[];
  status: AsyncStatus;
  error: string | null;
};

type FilterState = {
  topic?: string | null;
  author_id?: number | null;
  search?: string | null;
  followed_only?: boolean;
  sort?: 'latest' | 'trending';
};

interface HydrateSavedPayload {
  ids: string[];
  items?: CommunityPost[];
}

export interface CommunityState {
  topics: CommunityTopic[];
  topicsStatus: AsyncStatus;
  topicsError: string | null;
  feedItems: CommunityPost[];
  feedStatus: AsyncStatus;
  feedError: string | null;
  feedCursor: string | null;
  feedHasMore: boolean;
  feedFilters: FilterState;
  feedMode: 'for_you' | 'following' | 'latest';
  composerStatus: AsyncStatus;
  composerError: string | null;
  editorStatus: AsyncStatus;
  editorError: string | null;
  currentPost: CommunityPost | null;
  currentPostStatus: AsyncStatus;
  currentPostError: string | null;
  comments: Record<string, CommentsEntry>;
  deletionStatus: Record<string, AsyncStatus>;
  deletionError: Record<string, string | null>;
  trendingTopics: ReturnType<typeof initialTrendingTopics>;
  trendingStatus: AsyncStatus;
  trendingError: string | null;
  savedPostIds: string[];
  savedPosts: Record<string, CommunityPost>;
  dmThreads: CommunityDirectMessageThread[];
  dmThreadsStatus: AsyncStatus;
  dmThreadsError: string | null;
  dmMessages: Record<string, DirectMessageEntry>;
  dmSendStatus: Record<string, AsyncStatus>;
}

const initialTrendingTopics = () => [] as CommunityTrendingTopic[];

const initialCommentsEntry = (): CommentsEntry => ({
  items: [],
  status: 'idle',
  error: null,
  createStatus: 'idle',
  createError: null,
  likePending: {},
});

const ensureCommentsEntry = (entry: CommentsEntry): CommentsEntry => {
  if (!entry.likePending) {
    entry.likePending = {};
  }
  return entry;
};

const initialDmMessageEntry = (): DirectMessageEntry => ({
  items: [],
  status: 'idle',
  error: null,
});

const ensureDmMessageEntry = (entry: DirectMessageEntry): DirectMessageEntry => {
  return entry;
};

const sortDmThreads = (threads: CommunityDirectMessageThread[]): CommunityDirectMessageThread[] => {
  return [...threads].sort((a, b) => {
    const left = a.last_message_at ?? a.updated_at ?? '';
    const right = b.last_message_at ?? b.updated_at ?? '';
    if (left === right) {
      return (b.thread_id || '').localeCompare(a.thread_id || '');
    }
    return right.localeCompare(left);
  });
};

const initialState: CommunityState = {
  topics: [],
  topicsStatus: 'idle',
  topicsError: null,
  feedItems: [],
  feedStatus: 'idle',
  feedError: null,
  feedCursor: null,
  feedHasMore: true,
  feedFilters: {},
  feedMode: 'for_you',
  composerStatus: 'idle',
  composerError: null,
  editorStatus: 'idle',
  editorError: null,
  currentPost: null,
  currentPostStatus: 'idle',
  currentPostError: null,
  comments: {},
  deletionStatus: {},
  deletionError: {},
  trendingTopics: initialTrendingTopics(),
  trendingStatus: 'idle',
  trendingError: null,
  savedPostIds: [],
  savedPosts: {},
  dmThreads: [],
  dmThreadsStatus: 'idle',
  dmThreadsError: null,
  dmMessages: {},
  dmSendStatus: {},
};

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) {
      return detail;
    }
    if (typeof error.message === 'string' && error.message.length > 0) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return '网络请求出现异常，请稍后再试';
};

interface FeedRequest extends CommunityFeedParams {
  append?: boolean;
}

interface FeedResponsePayload {
  response: CommunityPostListResponse;
  append: boolean;
  filters: FilterState;
}

export const fetchCommunityTopicsThunk = createAsyncThunk<CommunityTopic[]>(
  'community/fetchTopics',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCommunityTopics();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchCommunityFeedThunk = createAsyncThunk<FeedResponsePayload, FeedRequest | undefined>(
  'community/fetchFeed',
  async (params, { rejectWithValue }) => {
    try {
      const { append = false, ...rest } = params || {};
      const response = await fetchCommunityPosts(rest);
      const filters: FilterState = {
        topic: rest?.topic ?? null,
        author_id: typeof rest?.author_id === 'number' ? rest.author_id : null,
        search: rest?.search ?? null,
        followed_only: rest?.followed_only ?? false,
          sort: rest?.sort ?? 'latest',
      };
      return { response, append, filters };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createCommunityPostThunk = createAsyncThunk<CommunityPost, CommunityPostCreatePayload>(
  'community/createPost',
  async (payload, { rejectWithValue }) => {
    try {
      return await createCommunityPost(payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchTrendingTopicsThunk = createAsyncThunk<CommunityTrendingTopic[]>(
  'community/fetchTrendingTopics',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchTrendingTopics(8);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchCommunityPostThunk = createAsyncThunk<CommunityPost, string>(
  'community/fetchPost',
  async (postId, { rejectWithValue }) => {
    try {
      return await fetchCommunityPost(postId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateCommunityPostThunk = createAsyncThunk<CommunityPost, { postId: string; payload: CommunityPostUpdatePayload }>(
  'community/updatePost',
  async ({ postId, payload }, { rejectWithValue }) => {
    try {
      return await updateCommunityPost(postId, payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteCommunityPostThunk = createAsyncThunk<string, string>(
  'community/deletePost',
  async (postId, { rejectWithValue }) => {
    try {
      await deleteCommunityPost(postId);
      return postId;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchCommunityCommentsThunk = createAsyncThunk<
  { postId: string; comments: CommunityComment[] },
  { postId: string; limit?: number; offset?: number }
>(
  'community/fetchComments',
  async ({ postId, ...rest }, { rejectWithValue }) => {
    try {
      const comments = await fetchCommunityComments(postId, rest);
      return { postId, comments };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createCommunityCommentThunk = createAsyncThunk<CommunityComment, { postId: string; payload: CommunityCommentPayload }>(
  'community/createComment',
  async ({ postId, payload }, { rejectWithValue }) => {
    try {
      return await createCommunityComment(postId, payload);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteCommunityCommentThunk = createAsyncThunk<
  { postId: string; commentId: string; removed: number },
  { postId: string; commentId: string }
>(
  'community/deleteComment',
  async ({ postId, commentId }, { rejectWithValue }) => {
    try {
      const result = await deleteCommunityComment(postId, commentId);
      return { postId, commentId, removed: result.removed };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const likeCommunityCommentThunk = createAsyncThunk<
  CommunityComment,
  { postId: string; commentId: string }
>(
  'community/likeComment',
  async ({ postId, commentId }, { rejectWithValue }) => {
    try {
      return await likeCommunityComment(postId, commentId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const unlikeCommunityCommentThunk = createAsyncThunk<
  CommunityComment,
  { postId: string; commentId: string }
>(
  'community/unlikeComment',
  async ({ postId, commentId }, { rejectWithValue }) => {
    try {
      return await unlikeCommunityComment(postId, commentId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const likeCommunityPostThunk = createAsyncThunk<CommunityPost, string>(
  'community/likePost',
  async (postId, { rejectWithValue }) => {
    try {
      return await likeCommunityPost(postId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const unlikeCommunityPostThunk = createAsyncThunk<CommunityPost, string>(
  'community/unlikePost',
  async (postId, { rejectWithValue }) => {
    try {
      return await unlikeCommunityPost(postId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const followCommunityAuthorThunk = createAsyncThunk<{ authorId: number; following: boolean }, number>(
  'community/followAuthor',
  async (authorId, { rejectWithValue }) => {
    try {
      const following = await followAuthor(authorId);
      return { authorId, following };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const unfollowCommunityAuthorThunk = createAsyncThunk<{ authorId: number; following: boolean }, number>(
  'community/unfollowAuthor',
  async (authorId, { rejectWithValue }) => {
    try {
      const following = await unfollowAuthor(authorId);
      return { authorId, following };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchDirectMessageThreadsThunk = createAsyncThunk<CommunityDirectMessageThread[]>(
  'community/fetchDirectMessageThreads',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchDirectMessageThreads();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const ensureDirectMessageThreadThunk = createAsyncThunk<CommunityDirectMessageThread, number>(
  'community/ensureDirectMessageThread',
  async (userId, { rejectWithValue }) => {
    try {
      return await ensureDirectMessageThread(userId);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchDirectMessagesThunk = createAsyncThunk<
  { threadId: string; messages: CommunityDirectMessage[] },
  { threadId: string; limit?: number }
>(
  'community/fetchDirectMessages',
  async ({ threadId, limit }, { rejectWithValue }) => {
    try {
      const messages = await fetchDirectMessages(threadId, { limit });
      return { threadId, messages };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const sendDirectMessageThunk = createAsyncThunk<
  { threadId: string; message: CommunityDirectMessage },
  { threadId: string; content: string }
>(
  'community/sendDirectMessage',
  async ({ threadId, content }, { rejectWithValue }) => {
    try {
      const message = await sendDirectMessage(threadId, content);
      return { threadId, message };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const upsertFeedPost = (state: CommunityState, updatedPost: CommunityPost) => {
  const index = state.feedItems.findIndex((item) => item.post_id === updatedPost.post_id);
  if (index >= 0) {
    state.feedItems[index] = updatedPost;
  } else {
    state.feedItems.unshift(updatedPost);
  }
};

const ensureViewerState = (post: CommunityPost) => {
  if (!post.viewer) {
    post.viewer = {
      has_liked: false,
      follows_author: false,
    };
  }
  return post.viewer;
};

const applySavedFlag = (state: CommunityState, post: CommunityPost) => {
  const viewer = ensureViewerState(post);
  const isSaved = state.savedPostIds.includes(post.post_id);
  viewer.has_saved = isSaved;
  if (isSaved) {
    state.savedPosts[post.post_id] = post;
  }
};

const communitySlice = createSlice({
  name: 'community',
  initialState,
  reducers: {
    resetFeed(state) {
      state.feedItems = [];
      state.feedCursor = null;
      state.feedHasMore = true;
      state.feedStatus = 'idle';
      state.feedError = null;
    },
    setFeedMode(state, action: PayloadAction<CommunityState['feedMode']>) {
      state.feedMode = action.payload;
    },
    clearComposerError(state) {
      state.composerError = null;
    },
    clearEditorError(state) {
      state.editorError = null;
    },
    clearCommentState(state, action: PayloadAction<string>) {
      delete state.comments[action.payload];
    },
    hydrateSavedPosts(state, action: PayloadAction<HydrateSavedPayload>) {
      const ids = Array.from(new Set(action.payload.ids));
      state.savedPostIds = ids;
      state.savedPosts = {};
      action.payload.items?.forEach((post) => {
        const viewer = ensureViewerState(post);
        viewer.has_saved = true;
        state.savedPosts[post.post_id] = post;
      });
      state.feedItems = state.feedItems.map((item) => {
        applySavedFlag(state, item);
        return item;
      });
      if (state.currentPost) {
        applySavedFlag(state, state.currentPost);
      }
    },
    toggleSavePost(state, action: PayloadAction<CommunityPost>) {
      const incoming = action.payload;
      const postId = incoming.post_id;
      const isSaved = state.savedPostIds.includes(postId);
      const newSavedIds = isSaved
        ? state.savedPostIds.filter((id) => id !== postId)
        : [postId, ...state.savedPostIds.filter((id) => id !== postId)];
      state.savedPostIds = newSavedIds;

      const updateViewerFlag = (post: CommunityPost | null | undefined) => {
        if (!post) {
          return;
        }
        const viewer = ensureViewerState(post);
        viewer.has_saved = !isSaved;
      };

      updateViewerFlag(state.currentPost?.post_id === postId ? state.currentPost : null);

      let sourcePost: CommunityPost | undefined;

      state.feedItems.forEach((item, index) => {
        if (item.post_id === postId) {
          updateViewerFlag(item);
          sourcePost = state.feedItems[index];
        }
      });

      if (!sourcePost && state.currentPost?.post_id === postId) {
        sourcePost = state.currentPost;
      }

      if (!sourcePost) {
        sourcePost = incoming;
        updateViewerFlag(sourcePost);
      }

      if (isSaved) {
        delete state.savedPosts[postId];
      } else if (sourcePost) {
        state.savedPosts[postId] = sourcePost;
      }
    },
    clearSavedPosts(state) {
      state.savedPostIds = [];
      state.savedPosts = {};
      state.feedItems.forEach((item) => {
        const viewer = ensureViewerState(item);
        viewer.has_saved = false;
      });
      if (state.currentPost) {
        const viewer = ensureViewerState(state.currentPost);
        viewer.has_saved = false;
      }
    },
    markDirectThreadRead(state, action: PayloadAction<string>) {
      const threadId = action.payload;
      const thread = state.dmThreads.find((item) => item.thread_id === threadId);
      if (thread) {
        thread.unread_count = 0;
      }
      const entry = state.dmMessages[threadId];
      if (entry) {
        entry.error = null;
      }
    },
    clearDirectMessages(state, action: PayloadAction<string | undefined>) {
      if (action.payload) {
        delete state.dmMessages[action.payload];
        delete state.dmSendStatus[action.payload];
      } else {
        state.dmMessages = {};
        state.dmSendStatus = {};
      }
    },
    clearDirectMessageError(state) {
      state.dmThreadsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommunityTopicsThunk.pending, (state) => {
        state.topicsStatus = 'loading';
        state.topicsError = null;
      })
      .addCase(fetchCommunityTopicsThunk.fulfilled, (state, action) => {
        state.topicsStatus = 'succeeded';
        state.topics = action.payload;
      })
      .addCase(fetchCommunityTopicsThunk.rejected, (state, action) => {
        state.topicsStatus = 'failed';
        state.topicsError = (action.payload as string) || '加载话题失败';
      })
      .addCase(fetchTrendingTopicsThunk.pending, (state) => {
        state.trendingStatus = 'loading';
        state.trendingError = null;
      })
      .addCase(fetchTrendingTopicsThunk.fulfilled, (state, action) => {
        state.trendingStatus = 'succeeded';
        state.trendingTopics = action.payload;
      })
      .addCase(fetchTrendingTopicsThunk.rejected, (state, action) => {
        state.trendingStatus = 'failed';
        state.trendingError = (action.payload as string) || '加载热门话题失败';
      })
      .addCase(fetchCommunityFeedThunk.pending, (state, action) => {
        if (action.meta.arg?.append) {
          state.feedStatus = state.feedStatus === 'idle' ? 'loading' : state.feedStatus;
        } else {
          state.feedStatus = 'loading';
          state.feedError = null;
        }
      })
      .addCase(fetchCommunityFeedThunk.fulfilled, (state, action) => {
        state.feedStatus = 'succeeded';
        const { response, append, filters } = action.payload;
        const incoming = response.items;
        const existingIds = new Set(state.feedItems.map((item) => item.post_id));
        state.feedFilters = filters;
        state.feedCursor = response.next_cursor ?? null;
        state.feedHasMore = response.has_more;

        if (append) {
          const newItems = incoming.filter((item) => !existingIds.has(item.post_id));
          newItems.forEach((item) => applySavedFlag(state, item));
          state.feedItems = [...state.feedItems, ...newItems];
        } else {
          state.feedItems = incoming.map((item) => {
            applySavedFlag(state, item);
            return item;
          });
        }
      })
      .addCase(fetchCommunityFeedThunk.rejected, (state, action) => {
        state.feedStatus = 'failed';
        state.feedError = (action.payload as string) || '加载社区动态失败';
      })
      .addCase(createCommunityPostThunk.pending, (state) => {
        state.composerStatus = 'loading';
        state.composerError = null;
      })
      .addCase(createCommunityPostThunk.fulfilled, (state, action) => {
        state.composerStatus = 'succeeded';
        state.composerError = null;
        applySavedFlag(state, action.payload);
        state.feedItems = [
          action.payload,
          ...state.feedItems.filter((item) => item.post_id !== action.payload.post_id),
        ];
      })
      .addCase(createCommunityPostThunk.rejected, (state, action) => {
        state.composerStatus = 'failed';
        state.composerError = (action.payload as string) || '发布帖子失败';
      })
      .addCase(fetchCommunityPostThunk.pending, (state) => {
        state.currentPostStatus = 'loading';
        state.currentPostError = null;
      })
      .addCase(fetchCommunityPostThunk.fulfilled, (state, action) => {
        state.currentPostStatus = 'succeeded';
        state.currentPost = action.payload;
        applySavedFlag(state, state.currentPost);
        upsertFeedPost(state, action.payload);
      })
      .addCase(fetchCommunityPostThunk.rejected, (state, action) => {
        state.currentPostStatus = 'failed';
        state.currentPostError = (action.payload as string) || '加载帖子详情失败';
      })
      .addCase(updateCommunityPostThunk.pending, (state) => {
        state.editorStatus = 'loading';
        state.editorError = null;
      })
      .addCase(updateCommunityPostThunk.fulfilled, (state, action) => {
        state.editorStatus = 'succeeded';
        state.editorError = null;
        state.currentPost = action.payload;
        applySavedFlag(state, state.currentPost);
        upsertFeedPost(state, action.payload);
      })
      .addCase(updateCommunityPostThunk.rejected, (state, action) => {
        state.editorStatus = 'failed';
        state.editorError = (action.payload as string) || '更新帖子失败';
      })
      .addCase(deleteCommunityPostThunk.pending, (state, action) => {
        const postId = action.meta.arg;
        state.deletionStatus[postId] = 'loading';
        state.deletionError[postId] = null;
      })
      .addCase(deleteCommunityPostThunk.fulfilled, (state, action) => {
        const postId = action.payload;
        state.deletionStatus[postId] = 'succeeded';
        state.feedItems = state.feedItems.filter((item) => item.post_id !== postId);
        if (state.currentPost?.post_id === postId) {
          state.currentPost = null;
          state.currentPostStatus = 'idle';
        }
        delete state.comments[postId];
      })
      .addCase(deleteCommunityPostThunk.rejected, (state, action) => {
        const postId = action.meta.arg;
        state.deletionStatus[postId] = 'failed';
        state.deletionError[postId] = (action.payload as string) || '删除帖子失败';
      })
      .addCase(fetchCommunityCommentsThunk.pending, (state, action) => {
        const postId = action.meta.arg.postId;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.status = 'loading';
        entry.error = null;
        entry.likePending = entry.likePending || {};
        state.comments[postId] = entry;
      })
      .addCase(fetchCommunityCommentsThunk.fulfilled, (state, action) => {
        const { postId, comments } = action.payload;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.status = 'succeeded';
        entry.items = comments;
        entry.likePending = {};
        state.comments[postId] = entry;
      })
      .addCase(fetchCommunityCommentsThunk.rejected, (state, action) => {
        const postId = action.meta.arg.postId;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.status = 'failed';
        entry.error = (action.payload as string) || '加载评论失败';
        state.comments[postId] = entry;
      })
      .addCase(createCommunityCommentThunk.pending, (state, action) => {
        const postId = action.meta.arg.postId;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.createStatus = 'loading';
        entry.createError = null;
        state.comments[postId] = entry;
      })
      .addCase(createCommunityCommentThunk.fulfilled, (state, action) => {
        const comment = action.payload;
  const entry = ensureCommentsEntry(state.comments[comment.post_id] ?? initialCommentsEntry());
        entry.createStatus = 'succeeded';
        entry.items = [...entry.items, comment];
        state.comments[comment.post_id] = entry;

        const post = state.feedItems.find((item) => item.post_id === comment.post_id);
        if (post) {
          post.stats = {
            ...post.stats,
            comments: (post.stats?.comments ?? 0) + 1,
          };
        }
        if (state.currentPost?.post_id === comment.post_id) {
          state.currentPost = {
            ...state.currentPost,
            stats: {
              ...state.currentPost.stats,
              comments: (state.currentPost.stats?.comments ?? 0) + 1,
            },
          };
        }
      })
      .addCase(createCommunityCommentThunk.rejected, (state, action) => {
        const postId = action.meta.arg.postId;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.createStatus = 'failed';
        entry.createError = (action.payload as string) || '发表评论失败';
        state.comments[postId] = entry;
      })
      .addCase(deleteCommunityCommentThunk.fulfilled, (state, action) => {
        const { postId, commentId, removed } = action.payload;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        const toRemove = new Set<string>([commentId]);
        let expanded = true;
        while (expanded) {
          expanded = false;
          entry.items.forEach((item) => {
            if (item.parent_comment_id && toRemove.has(item.parent_comment_id) && !toRemove.has(item.comment_id)) {
              toRemove.add(item.comment_id);
              expanded = true;
            }
          });
        }
        entry.items = entry.items.filter((item) => !toRemove.has(item.comment_id));
        toRemove.forEach((id) => {
          delete entry.likePending[id];
        });
        state.comments[postId] = entry;

        const post = state.feedItems.find((item) => item.post_id === postId);
        if (post && post.stats?.comments) {
          post.stats = {
            ...post.stats,
            comments: Math.max(0, post.stats.comments - (removed ?? toRemove.size)),
          };
        }
        if (state.currentPost?.post_id === postId && state.currentPost.stats?.comments) {
          state.currentPost = {
            ...state.currentPost,
            stats: {
              ...state.currentPost.stats,
              comments: Math.max(0, state.currentPost.stats.comments - (removed ?? toRemove.size)),
            },
          };
        }
      })
      .addCase(deleteCommunityCommentThunk.rejected, (state, action) => {
        const postId = action.meta.arg.postId;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.error = (action.payload as string) || '删除评论失败';
        state.comments[postId] = entry;
      })
      .addCase(likeCommunityCommentThunk.pending, (state, action) => {
        const { postId, commentId } = action.meta.arg;
  const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.likePending[commentId] = true;
        state.comments[postId] = entry;
      })
      .addCase(likeCommunityCommentThunk.fulfilled, (state, action) => {
        const updated = action.payload;
  const entry = ensureCommentsEntry(state.comments[updated.post_id] ?? initialCommentsEntry());
        delete entry.likePending[updated.comment_id];
        entry.items = entry.items.map((item) => (item.comment_id === updated.comment_id ? updated : item));
        state.comments[updated.post_id] = entry;
      })
      .addCase(likeCommunityCommentThunk.rejected, (state, action) => {
        const { postId, commentId } = action.meta.arg;
        const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        delete entry.likePending[commentId];
        entry.error = (action.payload as string) || '点赞评论失败';
        state.comments[postId] = entry;
      })
      .addCase(unlikeCommunityCommentThunk.pending, (state, action) => {
        const { postId, commentId } = action.meta.arg;
        const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        entry.likePending[commentId] = true;
        state.comments[postId] = entry;
      })
      .addCase(unlikeCommunityCommentThunk.fulfilled, (state, action) => {
        const updated = action.payload;
        const entry = ensureCommentsEntry(state.comments[updated.post_id] ?? initialCommentsEntry());
        delete entry.likePending[updated.comment_id];
        entry.items = entry.items.map((item) => (item.comment_id === updated.comment_id ? updated : item));
        state.comments[updated.post_id] = entry;
      })
      .addCase(unlikeCommunityCommentThunk.rejected, (state, action) => {
        const { postId, commentId } = action.meta.arg;
        const entry = ensureCommentsEntry(state.comments[postId] ?? initialCommentsEntry());
        delete entry.likePending[commentId];
        entry.error = (action.payload as string) || '取消点赞失败';
        state.comments[postId] = entry;
      })
      .addCase(likeCommunityPostThunk.fulfilled, (state, action) => {
        const post = action.payload;
        applySavedFlag(state, post);
        upsertFeedPost(state, post);
        if (state.currentPost?.post_id === post.post_id) {
          state.currentPost = post;
        }
      })
      .addCase(unlikeCommunityPostThunk.fulfilled, (state, action) => {
        const post = action.payload;
        applySavedFlag(state, post);
        upsertFeedPost(state, post);
        if (state.currentPost?.post_id === post.post_id) {
          state.currentPost = post;
        }
      })
      .addCase(followCommunityAuthorThunk.fulfilled, (state, action) => {
        const { authorId, following } = action.payload;
        state.feedItems = state.feedItems.map((item) =>
          item.author.id === authorId
            ? { ...item, viewer: { ...item.viewer, follows_author: following } }
            : item,
        );
        if (state.currentPost?.author.id === authorId) {
          state.currentPost = {
            ...state.currentPost,
            viewer: { ...state.currentPost.viewer, follows_author: following },
          };
        }
      })
      .addCase(unfollowCommunityAuthorThunk.fulfilled, (state, action) => {
        const { authorId, following } = action.payload;
        state.feedItems = state.feedItems.map((item) =>
          item.author.id === authorId
            ? { ...item, viewer: { ...item.viewer, follows_author: following } }
            : item,
        );
        if (state.currentPost?.author.id === authorId) {
          state.currentPost = {
            ...state.currentPost,
            viewer: { ...state.currentPost.viewer, follows_author: following },
          };
        }
      })
      .addCase(fetchDirectMessageThreadsThunk.pending, (state) => {
        state.dmThreadsStatus = 'loading';
        state.dmThreadsError = null;
      })
      .addCase(fetchDirectMessageThreadsThunk.fulfilled, (state, action) => {
        state.dmThreadsStatus = 'succeeded';
        state.dmThreads = sortDmThreads(action.payload);
      })
      .addCase(fetchDirectMessageThreadsThunk.rejected, (state, action) => {
        state.dmThreadsStatus = 'failed';
        state.dmThreadsError = (action.payload as string) || action.error.message || '加载私信失败';
      })
      .addCase(ensureDirectMessageThreadThunk.pending, (state) => {
        state.dmThreadsError = null;
      })
      .addCase(ensureDirectMessageThreadThunk.fulfilled, (state, action) => {
        const incoming = action.payload;
        const index = state.dmThreads.findIndex((item) => item.thread_id === incoming.thread_id);
        if (index >= 0) {
          state.dmThreads[index] = { ...state.dmThreads[index], ...incoming };
        } else {
          state.dmThreads = [incoming, ...state.dmThreads];
        }
        state.dmThreads = sortDmThreads(state.dmThreads);
      })
      .addCase(ensureDirectMessageThreadThunk.rejected, (state, action) => {
        state.dmThreadsError = (action.payload as string) || action.error.message || '创建私信会话失败';
      })
      .addCase(fetchDirectMessagesThunk.pending, (state, action) => {
        const threadId = action.meta.arg.threadId;
        const entry = ensureDmMessageEntry(state.dmMessages[threadId] ?? initialDmMessageEntry());
        entry.status = 'loading';
        entry.error = null;
        state.dmMessages[threadId] = entry;
      })
      .addCase(fetchDirectMessagesThunk.fulfilled, (state, action) => {
        const { threadId, messages } = action.payload;
        const entry = ensureDmMessageEntry(state.dmMessages[threadId] ?? initialDmMessageEntry());
        entry.status = 'succeeded';
        entry.items = messages;
        entry.error = null;
        state.dmMessages[threadId] = entry;
        const thread = state.dmThreads.find((item) => item.thread_id === threadId);
        if (thread) {
          thread.unread_count = 0;
        }
      })
      .addCase(fetchDirectMessagesThunk.rejected, (state, action) => {
        const threadId = action.meta.arg.threadId;
        const entry = ensureDmMessageEntry(state.dmMessages[threadId] ?? initialDmMessageEntry());
        entry.status = 'failed';
        entry.error = (action.payload as string) || action.error.message || '加载私信内容失败';
        state.dmMessages[threadId] = entry;
      })
      .addCase(sendDirectMessageThunk.pending, (state, action) => {
        const threadId = action.meta.arg.threadId;
        state.dmSendStatus[threadId] = 'loading';
      })
      .addCase(sendDirectMessageThunk.fulfilled, (state, action) => {
        const { threadId, message } = action.payload;
        state.dmSendStatus[threadId] = 'succeeded';
        const entry = ensureDmMessageEntry(state.dmMessages[threadId] ?? initialDmMessageEntry());
        entry.items = [...entry.items, message];
        entry.status = entry.status === 'idle' ? 'succeeded' : entry.status;
        entry.error = null;
        state.dmMessages[threadId] = entry;

        const thread = state.dmThreads.find((item) => item.thread_id === threadId);
        const messageCreatedAt = message.created_at ?? new Date().toISOString();
        const messagePreview = message.content.length > 120 ? `${message.content.slice(0, 120)}…` : message.content;
        if (thread) {
          thread.last_message_preview = messagePreview;
          thread.last_message_at = messageCreatedAt;
          thread.updated_at = messageCreatedAt;
          thread.unread_count = 0;
        }
        state.dmThreads = sortDmThreads(state.dmThreads);
      })
      .addCase(sendDirectMessageThunk.rejected, (state, action) => {
        const threadId = action.meta.arg.threadId;
        state.dmSendStatus[threadId] = 'failed';
        const entry = ensureDmMessageEntry(state.dmMessages[threadId] ?? initialDmMessageEntry());
        entry.error = (action.payload as string) || action.error.message || '发送私信失败';
        state.dmMessages[threadId] = entry;
      });
  },
});

export const {
  resetFeed,
  clearComposerError,
  clearEditorError,
  clearCommentState,
  setFeedMode,
  hydrateSavedPosts,
  toggleSavePost,
  clearSavedPosts,
  markDirectThreadRead,
  clearDirectMessages,
  clearDirectMessageError,
} = communitySlice.actions;

export default communitySlice.reducer;
